import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import routeConfiguration from '../../routeConfiguration';
import config from '../../config';
import { createResourceLocatorString } from '../../util/routes';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { ensureCurrentUser } from '../../util/data';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { loadData } from './DashboardPage.duck';
import { getStripeConnectAccountLink } from '../../ducks/stripeConnectAccount.duck';
import { updateFavoriteProfiles, updateFavoriteListings } from '../../ducks/user.duck';
import { withViewport } from '../../util/contextHelpers';

import {
  AvatarDashboard,
  AvatarMedium,
  Page,
  Footer,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  ModalCompleteProfile,
  NamedLink,
  ExternalLink,
  StripeConnectAccountStatusBox,
  RecommendCustomerContainer,
  ResponsiveImage,
} from '../../components';

// Icon imports grouped
import { BsCheckCircle, BsCheckCircleFill } from 'react-icons/bs';
import { MdOutlineArticle } from 'react-icons/md';
import { AiOutlinePlayCircle } from 'react-icons/ai';
import { IoBriefcaseOutline, IoSettingsOutline } from 'react-icons/io5';
import { BiMessageRounded } from 'react-icons/bi';
import { CgProfile } from 'react-icons/cg';
import { TbLayoutList } from 'react-icons/tb';
import { FiCompass } from 'react-icons/fi';
import { PiHeadsetBold, PiLightning } from 'react-icons/pi';

import { IoIosArrowDown, IoIosArrowUp, IoIosArrowForward } from 'react-icons/io';
import { FaHeart, FaRegHeart, FaArrowRight } from 'react-icons/fa';

import { TopbarContainer } from '../../containers';
import css from './DashboardPage.css';
import { createSlug } from '../../util/urlHelpers';
import { trackIntercomEvent, updateIntercomUser } from '../../util/intercom';

import browseAvatar1 from '../../assets/dashboardpage-browsefreelancerscard-avatar-1.webp';
import browseAvatar2 from '../../assets/dashboardpage-browsefreelancerscard-avatar-2.webp';
import browseAvatar3 from '../../assets/dashboardpage-browsefreelancerscard-avatar-3.webp';

// Constants
const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';
const MAX_MOBILE_SCREEN_WIDTH = 720;

// External URLs
const HELP_URLS = {
  FREELANCER_GUIDES:
    'https://help.freedomly.io/fi/collections/3885878-palveluiden-myyminen-ohjeita-freelancerille',
  CUSTOMER_GUIDES:
    'https://help.freedomly.io/fi/collections/3778256-palveluiden-ostaminen-ohjeita-asiakkaalle',
  YOUTUBE_PLAYLIST: 'https://www.youtube.com/playlist?list=PL_STUE7EQFofQeNkHRn14qkLxS0OkuY0U',
  CALENDLY_DEMO:
    'https://calendly.com/miikka-freedomly/freedomly-demotapaaminen-20min?month=2023-03',
};

// Utility functions
const createReturnURL = (returnURLType, rootURL, routes) => {
  const path = createResourceLocatorString(
    'StripePayoutOnboardingPage',
    routes,
    { returnURLType },
    {}
  );
  const root = rootURL.replace(/\/$/, '');
  return `${root}${path}`;
};

const getStripeAccountData = stripeAccount => stripeAccount?.attributes?.stripeAccountData || null;

const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData?.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

const handleShowSpaceHome = () => {
  if (typeof window === 'object' && window.Intercom) {
    window.Intercom('showSpace', 'home');
  }
};

// Navigate to the public talent page, recording the visit in Intercom (via the
// backend API) so we can follow up with users who explored talent.
const navigateToTalentWithIntercom = currentUser => () => {
  const go = () => {
    window.location.href = '/talent';
  };

  if (currentUser?.id?.uuid && typeof window === 'object') {
    // Record the visit through the backend Intercom API, then navigate. The
    // user's Messenger identity is already established at login.
    Promise.all([
      updateIntercomUser({ visitedTalentPage: true }),
      trackIntercomEvent('Visited Talentpage from Dashboard'),
    ]).finally(go);
  } else {
    go();
  }
};

const handleGetStripeConnectAccountLinkFn = (getLinkFn, commonParams) => type => () => {
  getLinkFn({ type, ...commonParams })
    .then(url => {
      window.location.href = url;
    })
    .catch(err => console.error(err));
};

// FAQ data factory
const createFaqData = () => [
  {
    questionId: 'DashboardPage.sectionFAQQuestion1',
    answerId: 'DashboardPage.sectionFAQAnswer1',
    index: 0,
  },
  {
    questionId: 'DashboardPage.sectionFAQQuestion2',
    answerId: 'DashboardPage.sectionFAQAnswer2',
    index: 1,
  },
  {
    questionId: 'DashboardPage.sectionFAQQuestion3',
    answerId: 'DashboardPage.sectionFAQAnswer3',
    index: 2,
  },
  {
    questionId: 'DashboardPage.sectionFAQQuestion4',
    answerId: 'DashboardPage.sectionFAQAnswer4',
    index: 3,
    hasClickableLink: true,
  },
];

// Optimized FAQ Components
const FAQDropdownItem = React.memo(({ question, isOpen, onToggle }) => (
  <div className={css.faqItem} onClick={() => onToggle(question.index)}>
    <div className={css.faqItemClosed}>
      <FormattedMessage id={question.questionId} />
      {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
    </div>
    <div className={isOpen ? css.faqItemAnswerOpen : css.faqItemAnswerClosed}>
      {question.hasClickableLink ? (
        <FormattedMessage
          id={question.answerId}
          values={{
            lineBreak: <br />,
            b: chunks => <strong style={{ fontWeight: 700 }}>{chunks}</strong>,
            a: chunks => (
              <span style={{ color: '#1683a3' }} onClick={handleShowSpaceHome}>
                {chunks}
              </span>
            ),
          }}
        />
      ) : (
        <FormattedMessage
          id={question.answerId}
          values={{
            lineBreak: <br />,
            b: chunks => <strong style={{ fontWeight: 700 }}>{chunks}</strong>,
          }}
        />
      )}
    </div>
  </div>
));

const FaqDropdown = React.memo(({ faqData, openItems, onToggle }) => (
  <div className={css.faqItems}>
    {faqData.map(item => (
      <FAQDropdownItem
        key={item.index}
        question={item}
        isOpen={openItems.includes(item.index)}
        onToggle={onToggle}
      />
    ))}
  </div>
));

// Custom hooks
const useUserData = currentUser => {
  return useMemo(() => {
    const ensuredUser = ensureCurrentUser(currentUser);
    const profile = ensuredUser.attributes?.profile;
    const publicData = profile?.publicData || {};
    const protectedData = profile?.protectedData || {};

    return {
      ensuredUser,
      firstName: profile?.firstName,
      currentUserLoaded: !!ensuredUser.id,
      accountRole: publicData.account_role,
      employeeCount: protectedData.employeeCount || '1-10',
      hasProfileImage: !!currentUser?.profileImage?.id,
      hasBio: !!publicData.bio,
      isFreelancer: ['freelancer', 'light-entrepreneur'].includes(publicData.account_role),
      isCustomer: publicData.account_role === 'customer',
      isNotLE: ['freelancer', 'customer'].includes(publicData.account_role),
    };
  }, [currentUser]);
};

const useStripeData = (stripeAccount, currentUserLoaded) => {
  return useMemo(() => {
    const stripeConnected = currentUserLoaded && !!stripeAccount?.id;
    const stripeAccountData = stripeConnected ? getStripeAccountData(stripeAccount) : null;
    const requirementsMissing =
      stripeAccount &&
      (hasRequirements(stripeAccountData, 'past_due') ||
        hasRequirements(stripeAccountData, 'currently_due'));

    return {
      stripeConnected,
      stripeAccountData,
      requirementsMissing,
      accountId: stripeConnected ? stripeAccount.id : null,
    };
  }, [stripeAccount, currentUserLoaded]);
};

const useFaqState = () => {
  const [openFaqItems, setOpenFaqItems] = useState([]);
  const faqData = useMemo(() => createFaqData(), []);

  const toggleFaq = useCallback(index => {
    setOpenFaqItems(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  }, []);

  return { faqData, openFaqItems, toggleFaq };
};

const useFavoriteHandlers = (
  favoriteProfilesIds,
  favoriteListingsIds,
  onUpdateFavoriteProfiles,
  onUpdateFavoriteListings
) => {
  const handleRemoveFavoriteProfile = useCallback(
    id => {
      const newIds = favoriteProfilesIds.filter(profileId => profileId !== id);
      onUpdateFavoriteProfiles(newIds);
    },
    [favoriteProfilesIds, onUpdateFavoriteProfiles]
  );

  const handleRemoveFavoriteListing = useCallback(
    id => {
      const listingUuids = favoriteListingsIds.map(listingId => listingId.uuid);
      const newIds = listingUuids.filter(listingId => listingId !== id);
      onUpdateFavoriteListings(newIds);
    },
    [favoriteListingsIds, onUpdateFavoriteListings]
  );

  return { handleRemoveFavoriteProfile, handleRemoveFavoriteListing };
};

// Sidebar Components
const ProfileCard = React.memo(({ userData, notificationDot }) => (
  <div className={css.profileCard}>
    <div className={css.profileCardGreeting}>
      <AvatarDashboard user={userData.ensuredUser} disableProfileLink />
      <div className={css.profileCardGreetingText}>
        <span className={css.profileCardGreetingHello}>
          <FormattedMessage id="DashboardPage.welcomeGreeting" />
        </span>
        <span className={css.profileCardGreetingName}>{userData.firstName}</span>
      </div>
    </div>
    <div className={css.profileCardDivider} />
    <div className={css.profileCardLinks}>
      <NamedLink
        className={css.profileCardLink}
        name="InboxPage"
        params={{ tab: userData.isFreelancer ? 'sales' : 'orders' }}
      >
        <BiMessageRounded className={css.profileCardLinkIcon} />
        <span className={css.profileCardLinkText}>
          <FormattedMessage id="DashboardPage.inboxLink" />
        </span>
        {notificationDot}
      </NamedLink>
      <NamedLink
        name="ProfilePage"
        params={{ id: userData.ensuredUser?.id.uuid }}
        className={css.profileCardLink}
      >
        <CgProfile className={css.profileCardLinkIcon} />
        <span className={css.profileCardLinkText}>
          <FormattedMessage id="DashboardPage.profileLink" />
        </span>
      </NamedLink>
      {userData.isFreelancer ? (
        <NamedLink name="ManageListingsPage" className={css.profileCardLink}>
          <TbLayoutList className={css.profileCardLinkIcon} />
          <span className={css.profileCardLinkText}>
            <FormattedMessage id="DashboardPage.listingsLink" />
          </span>
        </NamedLink>
      ) : (
        <NamedLink name="ManageProjectListingsPage" className={css.profileCardLink}>
          <TbLayoutList className={css.profileCardLinkIcon} />
          <span className={css.profileCardLinkText}>
            <FormattedMessage id="DashboardPage.projectsLink" />
          </span>
        </NamedLink>
      )}
      {userData.isCustomer && (
        <NamedLink name="FavoritesPage" params={{ tab: 'all' }} className={css.profileCardLink}>
          <FaRegHeart className={css.profileCardLinkIcon} />
          <span className={css.profileCardLinkText}>
            <FormattedMessage id="DashboardPage.favoritesLink" />
          </span>
        </NamedLink>
      )}
      <NamedLink name="AccountSettingsPage" className={css.profileCardLink}>
        <IoSettingsOutline className={css.profileCardLinkIcon} />
        <span className={css.profileCardLinkText}>
          <FormattedMessage id="DashboardPage.profileSettingsLink" />
        </span>
      </NamedLink>
      <NamedLink name="BrowseFreelancersPage" className={css.profileCardLink}>
        <FiCompass className={css.profileCardLinkIcon} />
        <span className={css.profileCardLinkText}>
          <FormattedMessage id="DashboardPage.discoverLink" />
        </span>
      </NamedLink>
    </div>
  </div>
));

const ArticleBox = React.memo(({ isCustomer }) => (
  <div className={css.articleBox}>
    <div className={css.articleBoxHeader}>
      <FormattedMessage
        id={isCustomer ? 'DashboardPage.articleBoxTitleCustomer' : 'DashboardPage.articleBoxTitle'}
      />
    </div>
    <ExternalLink
      className={css.articleBoxArticle}
      href={isCustomer ? HELP_URLS.CUSTOMER_GUIDES : HELP_URLS.FREELANCER_GUIDES}
    >
      <MdOutlineArticle className={css.articleBoxArticleIcon} />
      <FormattedMessage
        id={isCustomer ? 'DashboardPage.articleBoxText1Customer' : 'DashboardPage.articleBoxText1'}
      />
    </ExternalLink>
    <div className={css.articleBoxArticle} onClick={handleShowSpaceHome}>
      <PiHeadsetBold className={css.articleBoxArticleIcon} />
      <FormattedMessage id="DashboardPage.articleBoxOpenChat" />
    </div>
    <ExternalLink className={css.articleBoxArticle} href={HELP_URLS.YOUTUBE_PLAYLIST}>
      <AiOutlinePlayCircle className={css.articleBoxArticleIcon} />
      <FormattedMessage id="DashboardPage.articleBoxText2" />
    </ExternalLink>
  </div>
));

const Sidebar = React.memo(({ userData, notificationDot, viewport }) => (
  <div className={css.sidebar}>
    <ProfileCard userData={userData} notificationDot={notificationDot} />
    {userData.isCustomer && (!userData.hasProfileImage || !userData.hasBio) && (
      <CustomerChecklist userData={userData} />
    )}
    {!userData.isCustomer && viewport.width >= MAX_MOBILE_SCREEN_WIDTH && (
      <ArticleBox isCustomer={false} />
    )}
    {userData.isCustomer && viewport.width >= MAX_MOBILE_SCREEN_WIDTH && (
      <ArticleBox isCustomer={true} />
    )}
    {!userData.isCustomer && viewport.width >= MAX_MOBILE_SCREEN_WIDTH && (
      <RecommendCustomerContainer />
    )}
  </div>
));

// Content Components
const CustomerChecklist = React.memo(({ userData }) => (
  <div className={css.customerChecklist}>
    <div className={css.customerChecklistHeader}>
      <FormattedMessage id="DashboardPage.customerChecklistHeader" />
    </div>
    <div className={css.customerChecklistContent}>
      <NamedLink name="ProfileSettingsPage" className={css.customerChecklistItem}>
        <BsCheckCircleFill
          className={
            userData.hasProfileImage
              ? css.customerChecklistItemIconChecked
              : css.customerChecklistItemIcon
          }
        />
        <FormattedMessage id="DashboardPage.customerChecklistItem1" />
      </NamedLink>
      <NamedLink name="ProfileSettingsPage" className={css.customerChecklistItem}>
        <BsCheckCircleFill
          className={
            userData.hasBio ? css.customerChecklistItemIconChecked : css.customerChecklistItemIcon
          }
        />
        <FormattedMessage id="DashboardPage.customerChecklistItem2" />
      </NamedLink>
    </div>
  </div>
));

const BrowseFreelancersCard = React.memo(() => (
  <NamedLink name="BrowseFreelancersPage" className={css.browseFreelancersCard}>
    <div className={css.browseFreelancersAvatars}>
      <img src={browseAvatar1} alt="" aria-hidden="true" className={css.browseFreelancersAvatar} />
      <img src={browseAvatar2} alt="" aria-hidden="true" className={css.browseFreelancersAvatar} />
      <img src={browseAvatar3} alt="" aria-hidden="true" className={css.browseFreelancersAvatar} />
    </div>
    <div className={css.browseFreelancersText}>
      <div className={css.browseFreelancersDescription}>
        <FormattedMessage id="DashboardPage.browseFreelancersDescription" />
      </div>
    </div>
    <IoIosArrowForward className={css.browseFreelancersArrow} />
  </NamedLink>
));

const FreelancerTiles = React.memo(() => (
  <div className={css.freelancerTiles}>
    <ModalCompleteProfile id="CompleteProfileReminder" className={css.completeProfile} />
    <BrowseFreelancersCard />

    <div className={css.freelancerJobTipTile}>
      <div className={css.freelancerJobTipHeader}>
        <h2 className={css.freelancerJobTipTileTitle}>
          <FormattedMessage id="DashboardPage.freelancerJobTipTileTitle" />
        </h2>
        <p className={css.freelancerJobTipTileSubtitle}>
          <FormattedMessage id="DashboardPage.freelancerJobTipTileSubtitle" />
        </p>
      </div>

      <div className={css.freelancerJobTipRows}>
        {[1, 2, 3].map(i => (
          <div key={i} className={css.freelancerJobTipRow}>
            <div className={css.freelancerJobTipIconWrap}>{i}</div>
            <div className={css.freelancerJobTipTextWrap}>
              <div className={css.freelancerJobTipRowTitle}>
                <FormattedMessage id={`DashboardPage.freelancerJobTipTileTitle${i}`} />
              </div>
              <div className={css.freelancerJobTipRowSub}>
                <FormattedMessage id={`DashboardPage.freelancerJobTipTileText${i}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
));

const BuyerCardHeader = React.memo(({ icon: Icon, titleId }) => (
  <div className={css.buyerCardHeader}>
    <div className={css.buyerTileHeaderIconContainer}>
      <Icon className={css.buyerTileHeaderIcon} />
    </div>
    <h3 className={css.buyerCardTitle}>
      <FormattedMessage id={titleId} />
    </h3>
  </div>
));

const BuyerCardSteps = React.memo(() => (
  <div className={css.buyerCardStepRow}>
    {[1, 2, 3].map(i => (
      <React.Fragment key={i}>
        <div className={css.buyerCardStep}>
          <span className={css.buyerCardStepNumber}>{`0${i}`}</span>
          <span className={css.buyerCardStepText}>
            <FormattedMessage id={`DashboardPage.buyerCardStep${i}`} />
          </span>
        </div>
        {i < 3 && (
          <div className={css.buyerCardStepConnector}>
            <IoIosArrowForward />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
));

const BuyerCardChips = React.memo(() => (
  <div className={css.buyerCardChips}>
    {[1, 2, 3].map(i => (
      <div key={i} className={css.buyerCardChip}>
        <BsCheckCircle className={css.buyerCardChipIcon} />
        <FormattedMessage id={`DashboardPage.buyerCardChip${i}`} />
      </div>
    ))}
  </div>
));

const BuyerTiles = React.memo(() => (
  <div className={css.buyerTiles}>
    <div className={css.buyerTileTitle}>
      <FormattedMessage id="DashboardPage.buyerTilesSectionTitle" />
    </div>

    {/* Card A — self-serve matchmaking via a job posting */}
    <div className={css.buyerCard}>
      <BuyerCardHeader icon={IoBriefcaseOutline} titleId="DashboardPage.buyerCardFindTitle" />
      <div className={css.buyerCardSubtitle}>
        <FormattedMessage id="DashboardPage.buyerCardFindSubtitle" />
      </div>
      <BuyerCardSteps />
      <NamedLink className={css.buyerTilePrimaryCTA} name="NewProjectListingPage">
        <FormattedMessage id="DashboardPage.buyerCardFindCTA" />
      </NamedLink>
    </div>

    {/* Card B — done-for-you Pro service */}
    <div className={css.buyerCard}>
      <BuyerCardHeader icon={PiLightning} titleId="DashboardPage.buyerCardDoneTitle" />
      <div className={css.buyerCardSubtitle}>
        <FormattedMessage id="DashboardPage.buyerCardDoneSubtitle" />
      </div>
      <BuyerCardChips />
      <div
        className={css.buyerTilePrimaryCTA}
        onClick={handleShowSpaceHome}
        style={{ cursor: 'pointer' }}
      >
        <FormattedMessage id="DashboardPage.buyerTileNewFreedomlyProCTA" />
      </div>
    </div>
  </div>
));

const TalentBanner = React.memo(({ currentUser }) => (
  <div className={css.talentBanner}>
    <div className={css.talentBannerAvatars}>
      <img src={browseAvatar1} alt="" aria-hidden="true" className={css.talentBannerAvatar} />
      <img src={browseAvatar2} alt="" aria-hidden="true" className={css.talentBannerAvatar} />
      <img src={browseAvatar3} alt="" aria-hidden="true" className={css.talentBannerAvatar} />
    </div>
    <div className={css.talentBannerText}>
      <div className={css.talentBannerTitle}>
        <FormattedMessage id="DashboardPage.talentBannerTitle" />
      </div>
      <div className={css.talentBannerDescription}>
        <FormattedMessage id="DashboardPage.talentBannerDescription" />
      </div>
    </div>
    <div
      className={css.talentBannerCTA}
      onClick={navigateToTalentWithIntercom(currentUser)}
      style={{ cursor: 'pointer' }}
    >
      <FormattedMessage id="DashboardPage.talentBannerCTA" />
    </div>
  </div>
));

const UserTypeSpecificContent = React.memo(({ userData }) => (
  <>{userData.isCustomer ? <BuyerTiles /> : <FreelancerTiles />}</>
));

const FavoriteListingItem = React.memo(({ listing, onRemove }) => (
  <NamedLink
    className={css.interestListingsContainer}
    name="ListingPage"
    params={{ id: listing.id.uuid, slug: createSlug(listing.attributes.title) }}
  >
    <div className={css.interestListingsContainerInfo}>
      <ResponsiveImage
        rootClassName={css.rootForImage}
        alt={listing.attributes.title}
        image={listing.images[0]}
        variants={['landscape-crop', 'landscape-crop2x']}
      />
      <div className={css.interestListingsInfo}>
        <div className={css.interestListingsTitle}>{listing.attributes.title}</div>
        <div className={css.interestListingsAuthor}>
          {listing.author.attributes.profile.displayName}
        </div>
      </div>
    </div>
    <FaHeart
      className={css.favoriteIcon}
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
        onRemove(listing.id.uuid);
      }}
    />
  </NamedLink>
));

const FavoriteProfileItem = React.memo(({ listing, onRemove }) => (
  <NamedLink
    className={css.interestListingsContainer}
    name="ProfilePage"
    params={{ id: listing.author.id.uuid }}
  >
    <div className={css.interestListingsContainerInfo}>
      <AvatarMedium rootClassName={css.favoriteAvatar} user={listing.author} disableProfileLink />
      <div className={css.interestListingsInfo}>
        <div className={css.interestListingsTitle}>
          {listing.author.attributes.profile.displayName}
        </div>
        <div className={css.interestListingsAuthor}>
          {listing.attributes.publicData.authorWhoAmI}
        </div>
      </div>
    </div>
    <FaHeart
      className={css.favoriteIcon}
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
        onRemove(listing.author.id.uuid);
      }}
    />
  </NamedLink>
));

const FavoriteSection = React.memo(
  ({ favoriteListings, favoriteProfileListings, favoriteHandlers }) => {
    // Deduplicate profile listings by author ID
    const uniqueProfileListings = useMemo(() => {
      const seen = new Set();
      return favoriteProfileListings.filter(listing => {
        const authorId = listing.author?.id?.uuid;
        if (!authorId || seen.has(authorId)) {
          return false;
        }
        seen.add(authorId);
        return true;
      });
    }, [favoriteProfileListings]);

    // Deduplicate regular listings by listing ID
    const uniqueListings = useMemo(() => {
      const seen = new Set();
      return favoriteListings
        .filter(l => l.attributes.publicData.listingType === 'service')
        .filter(listing => {
          const listingId = listing.id?.uuid;
          if (!listingId || seen.has(listingId)) {
            return false;
          }
          seen.add(listingId);
          return true;
        });
    }, [favoriteListings]);

    return (
      <div className={css.interestContainer}>
        <div className={css.interesListings}>
          <div className={css.interestListingsHeader}>
            <FormattedMessage id="DashboardPage.favoriteListingsHeader" />
            <NamedLink
              className={css.showAllFavorites}
              name="FavoritesPage"
              params={{ tab: 'all' }}
            >
              <FormattedMessage id="DashboardPage.favoriteListingsShowAll" />
              <FaArrowRight />
            </NamedLink>
          </div>

          {uniqueProfileListings.slice(0, uniqueListings.length === 0 ? 4 : 2).map(listing => (
            <FavoriteProfileItem
              key={`profile-${listing.author.id.uuid}`}
              listing={listing}
              onRemove={favoriteHandlers.handleRemoveFavoriteProfile}
            />
          ))}

          {uniqueListings.slice(0, 2).map(listing => (
            <FavoriteListingItem
              key={`listing-${listing.id.uuid}`}
              listing={listing}
              onRemove={favoriteHandlers.handleRemoveFavoriteListing}
            />
          ))}
        </div>
      </div>
    );
  }
);

// MainContent component
const MainContent = React.memo(
  ({
    userData,
    stripeData,
    businessLogic,
    favoriteListings,
    favoriteProfileListings,
    favoriteHandlers,
    faqData,
    openFaqItems,
    toggleFaq,
    handleGetStripeConnectAccountLink,
    getAccountLinkInProgress,
    viewport,
    currentUser,
  }) => (
    <div className={css.contentContainer}>
      {stripeData.stripeConnected &&
        !businessLogic.returnedAbnormallyFromStripe &&
        businessLogic.showVerificationNeeded &&
        userData.isNotLE && (
          <StripeConnectAccountStatusBox
            type="verificationNeeded"
            inProgress={getAccountLinkInProgress}
            onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink(
              'custom_account_verification'
            )}
          />
        )}

      <UserTypeSpecificContent userData={userData} />

      {userData.isCustomer && <TalentBanner currentUser={currentUser} />}

      {userData.isCustomer &&
        (favoriteListings.length > 0 || favoriteProfileListings.length > 0) && (
          <FavoriteSection
            favoriteListings={favoriteListings}
            favoriteProfileListings={favoriteProfileListings}
            favoriteHandlers={favoriteHandlers}
          />
        )}

      {userData.isCustomer && (
        <div className={css.faqContainer}>
          <div className={css.faqHeader}>
            <FormattedMessage id="DashboardPage.sectionFAQTitle" />
          </div>
          <FaqDropdown faqData={faqData} openItems={openFaqItems} onToggle={toggleFaq} />
        </div>
      )}

      {!userData.isCustomer && viewport.width < MAX_MOBILE_SCREEN_WIDTH && (
        <RecommendCustomerContainer />
      )}

      {viewport.width <= MAX_MOBILE_SCREEN_WIDTH && <ArticleBox isCustomer={userData.isCustomer} />}
    </div>
  )
);

// Main Dashboard Component
export const DashboardPageComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    favoriteProfileListings,
    getAccountLinkInProgress,
    stripeAccount,
    onGetStripeConnectAccountLink,
    params,
    viewport,
    onUpdateFavoriteListings,
    onUpdateFavoriteProfiles,
    favoriteListingsIds,
    favoriteProfilesIds,
  } = props;

  // Custom hooks
  const userData = useUserData(currentUser);
  const stripeData = useStripeData(stripeAccount, userData.currentUserLoaded);
  const { faqData, openFaqItems, toggleFaq } = useFaqState();
  const favoriteHandlers = useFavoriteHandlers(
    favoriteProfilesIds,
    favoriteListingsIds,
    onUpdateFavoriteProfiles,
    onUpdateFavoriteListings
  );

  // Redux data
  const { favoriteListings, currentUserNotificationCount } = useSelector(store => {
    const { currentUserNotificationCount, favoriteListingsIds } = store.user;

    const favoriteListings = getListingsById(store, favoriteListingsIds || []);

    return {
      currentUserNotificationCount,
      favoriteListings,
    };
  });

  // Memoized computed values
  const urls = useMemo(() => {
    const rootURL = config.canonicalRootURL;
    const routes = routeConfiguration();
    return {
      successURL: createReturnURL(STRIPE_ONBOARDING_RETURN_URL_SUCCESS, rootURL, routes),
      failureURL: createReturnURL(STRIPE_ONBOARDING_RETURN_URL_FAILURE, rootURL, routes),
    };
  }, []);

  const businessLogic = useMemo(() => {
    const { returnURLType } = params;

    return {
      returnedAbnormallyFromStripe: returnURLType === STRIPE_ONBOARDING_RETURN_URL_FAILURE,
      showVerificationNeeded: stripeData.stripeConnected && stripeData.requirementsMissing,
    };
  }, [params, stripeData]);

  // Event handlers
  const handleGetStripeConnectAccountLink = useMemo(
    () =>
      handleGetStripeConnectAccountLinkFn(onGetStripeConnectAccountLink, {
        accountId: stripeData.accountId,
        successURL: urls.successURL,
        failureURL: urls.failureURL,
      }),
    [onGetStripeConnectAccountLink, stripeData.accountId, urls]
  );

  const notificationDot =
    currentUserNotificationCount > 0 ? <div className={css.notificationDot} /> : null;

  return (
    <Page title="Dashboard" scrollingDisabled={scrollingDisabled}>
      <LayoutWrapperTopbar>
        <TopbarContainer currentPage="DashboardPage" />
      </LayoutWrapperTopbar>
      {userData.currentUserLoaded && (
        <LayoutWrapperMain>
          <div className={css.dashboardMain}>
            <div className={css.dashboardMainContent}>
              <Sidebar userData={userData} notificationDot={notificationDot} viewport={viewport} />
              <MainContent
                userData={userData}
                stripeData={stripeData}
                businessLogic={businessLogic}
                favoriteListings={favoriteListings}
                favoriteProfileListings={favoriteProfileListings}
                favoriteHandlers={favoriteHandlers}
                faqData={faqData}
                openFaqItems={openFaqItems}
                toggleFaq={toggleFaq}
                handleGetStripeConnectAccountLink={handleGetStripeConnectAccountLink}
                getAccountLinkInProgress={getAccountLinkInProgress}
                viewport={viewport}
                currentUser={currentUser}
              />
            </div>
          </div>
        </LayoutWrapperMain>
      )}
      <LayoutWrapperFooter>
        <Footer />
      </LayoutWrapperFooter>
    </Page>
  );
};

// PropTypes
const { bool, object } = PropTypes;

DashboardPageComponent.defaultProps = {
  filterConfig: config.custom.filters,
};

DashboardPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  history: object.isRequired,
  location: object.isRequired,
  intl: intlShape.isRequired,
};

// Redux connection
const mapStateToProps = state => {
  const {
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
  } = state.stripeConnectAccount;

  const {
    currentUser,
    hasAppropriateListing,
    favoriteProfileListingsIds,
    favoriteListingsIds,
    favoriteProfilesIds,
  } = state.user;

  const { payoutDetailsSaveInProgress, payoutDetailsSaved } = state.StripePayoutPage;

  // Safely get favorite profile listings
  const favoriteProfileListings = getListingsById(state, favoriteProfileListingsIds || []);

  return {
    currentUser,
    hasAppropriateListing,
    getAccountLinkInProgress,
    getAccountLinkError,
    createStripeAccountError,
    updateStripeAccountError,
    fetchStripeAccountError,
    stripeAccount,
    stripeAccountFetched,
    payoutDetailsSaveInProgress,
    payoutDetailsSaved,
    scrollingDisabled: isScrollingDisabled(state),
    favoriteProfileListings,
    favoriteListingsIds,
    favoriteProfileListingsIds,
    favoriteProfilesIds,
  };
};

const mapDispatchToProps = dispatch => ({
  onGetStripeConnectAccountLink: params => dispatch(getStripeConnectAccountLink(params)),
  onUpdateFavoriteProfiles: profileIds => dispatch(updateFavoriteProfiles(profileIds)),
  onUpdateFavoriteListings: listingIds => dispatch(updateFavoriteListings(listingIds)),
});

const DashboardPage = compose(
  withRouter,
  withViewport,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(DashboardPageComponent);

DashboardPage.loadData = loadData;

export default DashboardPage;
